import React, { useState } from "react";
import { useAuth } from "../state/AuthProvider";
import Card from "./Card";
import Input from "./Input";
import Button from "./Button";
import Checkbox from "./Checkbox";
import "../assets/styles.css";
import { isValidName, isValidEmail, toTitleCase } from "../utils";
import Spinner from './Spinner';
import SelectInput from "./SelectInput";
import { useTenant } from "../state/TenantProvider";

const GetStarted = () => {
  const NULL_NAME_MESSAGE = "Please enter your first name";
  const NAME_VALIDATE_MESSAGE = "Name is not valid";
  const EMAIL_VALIDATE_MESSAGE = "Please enter your email";
  const ROLE_VALIDATE_MESSAGE = "Please select an option";
  const CONSENT_VALIDATE_MESSAGE = "Please give consent to proceed";
  const DEFAULT_SELECT_VALUE = "default";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(DEFAULT_SELECT_VALUE);

  const [isConsentChecked, setIsConsentChecked] = useState(false);

  const [nameValidationMessage, setNameValidationMessage] = useState("");
  const [emailValidationMessage, setEmailValidationMessage] = useState("");
  const [roleValidationMessage, setRoleValidationMessage] = useState("");
  const [checkboxValidationMessage, setCheckboxValidationMessage] =
    useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { startSession, startSessionError } = useAuth();
  const { roles, isConfigLoaded, userRoleMessage } = useTenant();

  const rolesRequired = (roles.length > 0);

  const roleOptions = [
    {
      value: DEFAULT_SELECT_VALUE,
      label: userRoleMessage
    },
    ...(roles.map((r) => ({ value: r.id, label: r.role})).sort((a, b) => a.label.localeCompare(b.label)))
  ];

  const handleNameInputChange = (value) => {
    setNameValidationMessage("");
    setName(value);
  };

  const handleNameInputBlur = () => {
    if (!(name || "").trim()) {
      setNameValidationMessage(NULL_NAME_MESSAGE);
      return;
    }

    if (!isValidName(name)) {
      setNameValidationMessage(NAME_VALIDATE_MESSAGE);
      return;
    }
  };

  const handleRoleInputChange = (value) => {
    setRoleValidationMessage("");
    setRole(value);
  };

  const handleRoleInputBlur = () => {
    if (!role || role === DEFAULT_SELECT_VALUE) {
      setRoleValidationMessage(ROLE_VALIDATE_MESSAGE);
      return;
    }
  };

  const handleEmailInputChange = (value) => {
    setEmailValidationMessage("");
    setEmail(value);
  };

  const handleEmailInputBlur = () => {
    if (!email) {
      setEmailValidationMessage(EMAIL_VALIDATE_MESSAGE);
      return;
    }
    if (!isValidEmail(email)) {
      setEmailValidationMessage("Email address is not valid");
      return;
    }
  };

  const handleCheckboxChange = (checked) => {
    if (checked) {
      setCheckboxValidationMessage("");
    } else {
      setCheckboxValidationMessage(CONSENT_VALIDATE_MESSAGE);
    }

    setIsConsentChecked(checked);
  };

  const handleSubmit = async () => {
    if (!(name || "").trim()) {
      setNameValidationMessage(NULL_NAME_MESSAGE);
      return;
    }

    if (!isValidName(name)) {
      setNameValidationMessage(NAME_VALIDATE_MESSAGE);
      return;
    }

    if (!email || !isValidEmail(email)) {
      setEmailValidationMessage(EMAIL_VALIDATE_MESSAGE);
      return;
    }

    if ((!role || role === DEFAULT_SELECT_VALUE) && rolesRequired) {
      setRoleValidationMessage(ROLE_VALIDATE_MESSAGE);
      return;
    }

    if (!isConsentChecked) {
      setCheckboxValidationMessage(CONSENT_VALIDATE_MESSAGE);
      return;
    }

    if (emailValidationMessage || nameValidationMessage) return;

    try {
      setIsSubmitting(true);
      await startSession(toTitleCase(name.trim()), email.trim(), rolesRequired ? role : null);
    } catch (e) {
      console.log(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isConfigLoaded &&
        <Card>
          <div className="_kanis_card_title">Get started</div>
          <div className="_kanis_card_subtitle">
            Please enter your name and email address to get started
          </div>
          <Input
            name="name"
            value={name}
            placeholder="Your name"
            validationMessage={nameValidationMessage}
            onChange={(e) => handleNameInputChange(e.target.value)}
            onBlur={handleNameInputBlur}
            disabled={isSubmitting}
          />
          <Input
            name="email"
            value={email}
            placeholder="Email address"
            validationMessage={emailValidationMessage}
            onChange={(e) => handleEmailInputChange(e.target.value)}
            onBlur={handleEmailInputBlur}
            disabled={isSubmitting}
          />
          {rolesRequired &&
            <SelectInput 
              name="roles"
              value={role}
              validationMessage={roleValidationMessage}
              onChange={(e) => handleRoleInputChange(e.target.value)}
              onBlur={handleRoleInputBlur}
              disabled={isSubmitting}
              options={roleOptions}
            />
          }
          <Checkbox
            text="By entering this chat I agree to my messages being monitored"
            onChange={({ checked }) => handleCheckboxChange(checked)}
            validationMessage={checkboxValidationMessage}
            disabled={isSubmitting}
          />
          <Button type="primary" onClick={handleSubmit} disabled={isSubmitting} style={{ width: '90px' }}>
            {isSubmitting ? <Spinner size='small' /> : <span>Enter</span>}
          </Button>
          {startSessionError && <div className="_kanis_get_started_error">{startSessionError}</div>}
        </Card>
      }
      {!isConfigLoaded && <Spinner size='large' />}
    </>
  );
};

export default GetStarted;
